<template>
    <div class="step-rows">
        <ConfirmModal />
        <h3>3. Résultats</h3>

        <div class="mode-tabs">
            <button type="button" class="mode-tab" :class="{ active: mode === 'paste' }" @click="mode = 'paste'">
                Coller des noms
            </button>
            <button
                v-if="source === 'excel'"
                type="button"
                class="mode-tab"
                :class="{ active: mode === 'excel' }"
                @click="mode = 'excel'"
            >
                Fichier Excel
            </button>
            <button
                v-if="source === 'dartup'"
                type="button"
                class="mode-tab"
                :class="{ active: mode === 'dartup' }"
                @click="mode = 'dartup'"
            >
                Dartup
            </button>
            <button
                v-if="source === 'd4w'"
                type="button"
                class="mode-tab"
                :class="{ active: mode === 'd4w' }"
                @click="mode = 'd4w'"
            >
                Darts4Windows
            </button>
            <button
                v-if="source === '3kdarts'"
                type="button"
                class="mode-tab"
                :class="{ active: mode === '3kdarts' }"
                @click="mode = '3kdarts'"
            >
                3K Darts
            </button>
        </div>

        <div v-if="mode === 'paste'" class="paste-block">
            <div class="form-group">
                <label>Catégorie</label>
                <select v-model="pasteCategory" class="form-input">
                    <option v-for="c in RANKING_CATEGORIES" :value="c.value" :key="c.value">{{ c.label }}</option>
                </select>
            </div>

            <div class="subsection">
                <h4>Tableau</h4>
                <div class="form-group">
                    <label>Mode</label>
                    <select v-model="tournamentMode" class="form-input">
                        <option value="RR">Round-Robin (une seule liste ordonnée)</option>
                        <option value="AB">A/B (tableau A + repêchage B)</option>
                    </select>
                </div>
                <div class="form-group">
                    <label>{{ tournamentMode === 'AB' ? 'Liste A (meilleur en premier)' : 'Classement (meilleur en premier)' }}</label>
                    <textarea v-model="listAText" class="form-textarea" rows="8" placeholder="Un nom par ligne, du 1er au dernier"></textarea>
                </div>
                <div v-if="tournamentMode === 'AB'" class="form-group">
                    <label>Liste B (repêchage, optionnel)</label>
                    <textarea v-model="listBText" class="form-textarea" rows="6" placeholder="Un nom par ligne"></textarea>
                </div>
                <button type="button" class="btn-secondary" @click="addTableRows">Ajouter au tableau</button>
            </div>

            <div class="subsection">
                <h4>Poules uniquement</h4>
                <p class="hint">Joueurs jamais sortis des poules -- une ligne "Nom;victoires" par joueur.</p>
                <textarea v-model="poolsText" class="form-textarea" rows="5" placeholder="Jean Dupont;3&#10;Marie Martin;5"></textarea>
                <button type="button" class="btn-secondary" @click="addPoolsRows">Ajouter les poules</button>
            </div>
        </div>

        <div v-else-if="mode === 'excel'" class="excel-block">
            <p class="hint">
                Gabarit attendu : colonnes <strong>Catégorie</strong> / <strong>Joueur</strong> / <strong>Code CLT</strong>.
                Codes valides : 1, 2, 3, 5, 9, 17 (RR) -- A1..A17, B1..B5 (A/B) -- P0..P15 (poules).
            </p>
            <input type="file" accept=".xlsx,.xls" @change="onExcelFile" />
        </div>

        <div v-else-if="mode === 'dartup'" class="excel-block">
            <div class="mode-tabs">
                <button type="button" class="mode-tab" :class="{ active: dartupSource === 'link' }" @click="dartupSource = 'link'">
                    Lien Dartup
                </button>
                <button type="button" class="mode-tab" :class="{ active: dartupSource === 'file' }" @click="dartupSource = 'file'">
                    Fichier xlsx
                </button>
            </div>
            <p v-if="!params.eventId" class="hint">Sélectionnez d'abord le tournoi (étape précédente).</p>

            <template v-if="dartupSource === 'link'">
                <p class="hint">
                    Lien de recherche Dartup (ex. https://www.dartup.fr/public?q=crau&amp;date_from=…) ou lien d'un tableau.
                    Plus complet que l'export xlsx (équipes formées sur place). Seul le nom des joueurs est lu : club et
                    catégorie viennent de la licence ; doublettes reconstituées d'après les inscriptions.
                </p>
                <div class="dartup-link-row">
                    <input v-model="dartupUrl" class="form-input" placeholder="https://www.dartup.fr/public?q=..." />
                    <button
                        type="button"
                        class="btn-secondary"
                        :disabled="!dartupUrl || dartupLoading"
                        @click="searchDartupTournaments"
                    >
                        Chercher les tableaux
                    </button>
                </div>
                <table v-if="dartupTournaments.length" class="rows-table dartup-tournaments">
                    <thead>
                        <tr><th></th><th>Tableau Dartup</th><th>Date</th><th>Catégorie</th></tr>
                    </thead>
                    <tbody>
                        <tr v-for="t in dartupTournaments" :key="t.id">
                            <td><input v-model="t.selected" type="checkbox" /></td>
                            <td>{{ t.name }}</td>
                            <td>{{ t.date ?? '' }}</td>
                            <td>
                                <select v-model="t.category" class="form-input-sm">
                                    <option v-for="c in RANKING_CATEGORIES" :value="c.value" :key="c.value">{{ c.label }}</option>
                                </select>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <button
                    v-if="dartupTournaments.length"
                    type="button"
                    class="btn-secondary"
                    :disabled="!selectedTournamentCount || dartupLoading || !params.eventId"
                    @click="importDartupTournaments"
                >
                    {{ dartupLoading ? 'Lecture...' : `Importer ${selectedTournamentCount} tableau(x)` }}
                </button>
            </template>

            <template v-else>
            <p class="hint">
                Un export Dartup par tableau (plusieurs fichiers possibles). Seul le nom des joueurs est lu :
                club et catégorie viennent de la licence. Doublettes : le partenaire est repris de l'inscription.
                Attention : l'export xlsx omet les équipes formées sur place, préférer le lien.
            </p>
            <div class="form-group">
                <label>Catégorie</label>
                <select v-model="dartupCategory" class="form-input">
                    <option value="">Auto (d'après le titre du tableau)</option>
                    <option v-for="c in RANKING_CATEGORIES" :value="c.value" :key="c.value">{{ c.label }}</option>
                </select>
            </div>
            <input type="file" accept=".xlsx" multiple :disabled="dartupLoading || !params.eventId" @change="onDartupFiles" />
            </template>

            <ul v-if="dartupMessages.length" class="dartup-messages">
                <li v-for="(msg, idx) in dartupMessages" :key="idx">{{ msg }}</li>
            </ul>
        </div>

        <div v-else-if="mode === 'd4w'" class="excel-block">
            <p class="hint">
                <strong>Envoyez ici toutes les bases Darts4Windows (.dfw) du tournoi</strong>, en une seule fois ou
                l'une après l'autre : une seule base, une par jour (individuels, doubles) ou une par catégorie. Un
                tournoi ne s'importe qu'une fois, toutes catégories ensemble : un tableau oublié obligerait à
                supprimer l'import puis à le refaire en entier.
            </p>
            <p class="hint">
                Tableaux et poules sont lus ensemble ; un tournoi A + B (consolante des perdants du 1er tour du A)
                est regroupé en une ligne, codes A1… / B1…. Seul le nom des joueurs est repris, club et catégorie
                viennent de la licence. Vérifiez la catégorie de chaque tableau et décochez ceux qui ne comptent pas (tests,
                épreuve sans points).
            </p>
            <input type="file" accept=".dfw" multiple :disabled="d4wLoading" @change="onD4wFiles" />
            <table v-if="d4wTableaux.length" class="rows-table dartup-tournaments">
                <thead>
                    <tr><th></th><th>Fichier</th><th>Tableau</th><th>Poules</th><th>Date</th><th>Lignes</th><th>Catégorie</th></tr>
                </thead>
                <tbody>
                    <tr v-for="t in d4wTableaux" :key="`${t.file}/${t.key}`">
                        <td><input v-model="t.selected" type="checkbox" :disabled="t.imported" /></td>
                        <td>{{ t.file }}</td>
                        <td>{{ t.title }}<span v-if="t.imported" class="hint"> (ajouté)</span></td>
                        <td>{{ t.pool_title ?? '' }}</td>
                        <td>{{ t.date ?? '' }}</td>
                        <td>{{ t.rows.length }}</td>
                        <td>
                            <select v-model="t.category" class="form-input-sm">
                                <option v-for="c in RANKING_CATEGORIES" :value="c.value" :key="c.value">{{ c.label }}</option>
                            </select>
                        </td>
                    </tr>
                </tbody>
            </table>
            <button
                v-if="d4wTableaux.length"
                type="button"
                class="btn-secondary"
                :disabled="!selectedD4wCount"
                @click="importD4wTableaux"
            >
                Importer {{ selectedD4wCount }} tableau(x)
            </button>
            <ul v-if="d4wMessages.length" class="dartup-messages">
                <li v-for="(msg, idx) in d4wMessages" :key="idx">{{ msg }}</li>
            </ul>
        </div>

        <div v-else-if="mode === '3kdarts'" class="excel-block">
            <div class="mode-tabs">
                <button type="button" class="mode-tab" :class="{ active: threeKSource === 'link' }" @click="threeKSource = 'link'">
                    Lien 3K Darts
                </button>
                <button type="button" class="mode-tab" :class="{ active: threeKSource === 'paste' }" @click="threeKSource = 'paste'">
                    Copier-coller (secours)
                </button>
            </div>
            <p v-if="!params.eventId" class="hint">Sélectionnez d'abord le tournoi (étape précédente).</p>

            <template v-if="threeKSource === 'link'">
                <p class="hint">
                    Lien vers les résultats d'une seule catégorie sur le portail 3K (ex.
                    https://portal.3k-darts.com/frontend/events/5/event/32700/…) : toutes les catégories du même tournoi
                    sont retrouvées et cochées. Un lien de liste de tournois de l'API 3K (…/frontend/event/page?…) marche
                    aussi. Classement final et victoires de poules sont lus ensemble ; une épreuve « Tournoi B » compte
                    comme tableau B. Seul le nom des joueurs est lu : club et catégorie viennent de la licence.
                </p>
                <div class="dartup-link-row">
                    <input v-model="threeKUrl" class="form-input" placeholder="https://portal.3k-darts.com/frontend/events/..." />
                    <button
                        type="button"
                        class="btn-secondary"
                        :disabled="!threeKUrl || threeKLoading"
                        @click="searchThreeKEvents"
                    >
                        Chercher les épreuves
                    </button>
                </div>
                <table v-if="threeKEvents.length" class="rows-table dartup-tournaments">
                    <thead>
                        <tr><th></th><th>Épreuve 3K Darts</th><th>Date</th><th>Catégorie</th><th>Tableau</th></tr>
                    </thead>
                    <tbody>
                        <tr v-for="e in threeKEvents" :key="`${e.db_id}/${e.event_id}`">
                            <td><input v-model="e.selected" type="checkbox" /></td>
                            <td>{{ e.name }}</td>
                            <td>{{ e.date ?? '' }}</td>
                            <td>
                                <select v-model="e.category" class="form-input-sm">
                                    <option v-for="c in RANKING_CATEGORIES" :value="c.value" :key="c.value">{{ c.label }}</option>
                                </select>
                            </td>
                            <td>
                                <select v-model="e.list_type" class="form-input-sm">
                                    <option value="A">A</option>
                                    <option value="B">B</option>
                                </select>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <button
                    v-if="threeKEvents.length"
                    type="button"
                    class="btn-secondary"
                    :disabled="!selectedThreeKCount || threeKLoading || !params.eventId"
                    @click="importThreeKEvents"
                >
                    {{ threeKLoading ? 'Lecture...' : `Importer ${selectedThreeKCount} épreuve(s)` }}
                </button>
            </template>

            <template v-else>
            <p class="hint">
                Si le lien ne passe plus (3K a changé son filtrage) : copiez le classement depuis votre navigateur. Sur
                le portail 3K, ouvrez le classement final du tableau, appuyez sur F12, onglet
                <strong>Réseau</strong>, rechargez la page (F5), filtrez sur <code>placement</code>, cliquez sur la
                requête puis l'onglet <strong>Réponse</strong> : Ctrl+A, Ctrl+C, et collez ci-dessous. Un collage par
                tableau (A, puis B s'il y a un repêchage).
            </p>
            <div class="form-group">
                <label>Catégorie</label>
                <select v-model="threeKCategory" class="form-input">
                    <option v-for="c in RANKING_CATEGORIES" :value="c.value" :key="c.value">{{ c.label }}</option>
                </select>
            </div>
            <div class="form-group">
                <label>Tableau</label>
                <select v-model="threeKListType" class="form-input">
                    <option value="A">Tableau A (ou tableau unique)</option>
                    <option value="B">Tableau B (repêchage)</option>
                </select>
            </div>
            <div class="form-group">
                <label>Réponse "placement" copiée depuis 3K Darts</label>
                <textarea v-model="threeKText" class="form-textarea" rows="8" placeholder='[{"id": ..., "participant": {"displayName": "NOM, Prénom", ...}, "place": "1.", ...}]'></textarea>
            </div>
            <button type="button" class="btn-secondary" :disabled="!threeKText.trim()" @click="addThreeKRows">
                Ajouter au tableau
            </button>
            <div class="subsection">
                <h4>Victoires de poules</h4>
                <p class="hint">
                    Pour les sortis en poules (code CLT vide ci-dessus) : sur 3K, affichez tous les groupes de poules
                    d'un coup, filtrez l'onglet Réseau sur <code>round</code> et copiez la réponse (requête
                    <code>.../phase/…/round/0</code>). Un seul groupe à la fois marche aussi : collez-les à la suite.
                    Les joueurs sortis du tableau final gardent leur code.
                </p>
                <textarea v-model="threeKGroupsText" class="form-textarea" rows="5" placeholder='{"matches": [...], "tableInfo": {...}}'></textarea>
                <button type="button" class="btn-secondary" :disabled="!threeKGroupsText.trim()" @click="applyThreeKGroupWins">
                    Compléter les poules
                </button>
            </div>
            </template>
            <ul v-if="threeKMessages.length" class="dartup-messages">
                <li v-for="(msg, idx) in threeKMessages" :key="idx">{{ msg }}</li>
            </ul>
        </div>

        <div class="rows-table-wrapper">
            <div class="rows-toolbar">
                <h4>Lignes ({{ rows.length }})</h4>
                <select v-if="rows.length" v-model="categoryFilter" class="form-input-sm category-filter">
                    <option value="">Toutes les catégories</option>
                    <option v-for="c in presentCategories" :value="c.value" :key="c.value">
                        {{ c.label }} ({{ c.count }})
                    </option>
                </select>
                <span v-if="checking" class="hint">Comparaison aux inscriptions...</span>
                <template v-if="flaggedCount">
                    <span class="issue-cell">{{ flaggedCount }} non inscrit{{ flaggedCount > 1 ? 's' : '' }}</span>
                    <button
                        type="button"
                        class="btn-secondary btn-sm"
                        :disabled="registering"
                        @click="registerRows(rows.filter(isUnregistered))"
                    >
                        Inscrire les {{ flaggedCount }}
                    </button>
                </template>
                <span v-if="incompleteCount" class="issue-cell">
                    {{ incompleteCount }} doublette{{ incompleteCount > 1 ? 's' : '' }} incomplète{{ incompleteCount > 1 ? 's' : '' }}
                </span>
            </div>

            <div v-if="checkDone && filteredUnmatched.length" class="unmatched-block">
                <h4>Inscrits absents des résultats ({{ filteredUnmatched.length }})</h4>
                <p class="hint">
                    Absents le jour J, ou nom mal orthographié dans les résultats : dans ce cas, choisissez le bon
                    inscrit sur la ligne "non inscrit" correspondante ci-dessous.
                </p>
                <ul>
                    <li v-for="(u, idx) in filteredUnmatched" :key="idx">
                        <span class="unmatched-cat">{{ categoryLabel(u.category) }}</span> {{ u.name }}
                    </li>
                </ul>
            </div>

            <table v-if="rows.length" class="rows-table">
                <thead>
                    <tr>
                        <th>Catégorie</th>
                        <th>Joueur</th>
                        <th>Code CLT</th>
                        <th title="Règlement FFD : pas de points sans avoir gagné au moins un match (ex. exempté puis battu d'entrée)">
                            Aucun match gagné
                        </th>
                        <th></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="{ row, idx, unregistered, incomplete, unknownHalves, flagged } in displayedRows"
                        :class="{ 'row-issue': flagged }"
                        :key="idx"
                    >
                        <td>
                            <select v-model="row.category" class="form-input-sm" @change="checkRows([row])">
                                <option v-for="c in RANKING_CATEGORIES" :value="c.value" :key="c.value">{{ c.label }}</option>
                            </select>
                        </td>
                        <td>
                            <!-- @change : vérifié à la sortie du champ / Entrée, pas pendant la frappe -->
                            <input v-model="row.player_name_raw" class="form-input-sm name-input" @change="checkRows([row])" />
                            <template v-if="flagged">
                                <select
                                    v-for="u in unknownHalves"
                                    class="form-input-sm pick-inscription"
                                    @change="pickInscription(row, u.i, $event)"
                                    :key="u.i"
                                >
                                    <option value="">« {{ u.h }} » → choisir un inscrit…</option>
                                    <optgroup :label="`Absents des résultats (${categoryLabel(row.category)})`">
                                        <option v-for="e in candidateGroups(row).absent" :value="e.name" :key="e.id">{{ e.name }}</option>
                                    </optgroup>
                                    <optgroup label="Autres inscrits">
                                        <option v-for="e in candidateGroups(row).others" :value="e.name" :key="e.id">{{ e.name }}</option>
                                    </optgroup>
                                </select>
                                <select
                                    v-if="incomplete"
                                    class="form-input-sm pick-inscription"
                                    @change="pickInscription(row, null, $event)"
                                >
                                    <option value="">Ajouter le partenaire…</option>
                                    <optgroup :label="`Absents des résultats (${categoryLabel(row.category)})`">
                                        <option v-for="e in candidateGroups(row).absent" :value="e.name" :key="e.id">{{ e.name }}</option>
                                    </optgroup>
                                    <optgroup label="Autres inscrits">
                                        <option v-for="e in candidateGroups(row).others" :value="e.name" :key="e.id">{{ e.name }}</option>
                                    </optgroup>
                                </select>
                            </template>
                        </td>
                        <td><input v-model="row.code_clt" class="form-input-sm code-clt-input" /></td>
                        <td class="no-win-cell"><input v-model="row.no_match_won" type="checkbox" /></td>
                        <td class="issue-cell">
                            <div v-if="incomplete">partenaire manquant (« Nom1 / Nom2 »)</div>
                            <template v-if="unregistered">
                                non inscrit
                                <button
                                    type="button"
                                    class="btn-secondary btn-sm"
                                    :disabled="registering"
                                    @click="registerRows([row])"
                                >
                                    Inscrire
                                </button>
                            </template>
                        </td>
                        <td><button type="button" class="row-delete" @click="rows.splice(idx, 1)">×</button></td>
                    </tr>
                </tbody>
            </table>
            <p v-else class="hint">Aucune ligne pour le moment.</p>
        </div>

        <div class="form-actions">
            <button type="button" class="btn-secondary" @click="$emit('back')">← Retour</button>
            <button type="button" class="btn-submit" :disabled="!rows.length" @click="$emit('next')">
                Aperçu ({{ rows.length }} ligne{{ rows.length > 1 ? 's' : '' }})
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, shallowRef, computed, watch, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import dartsHubApi from '@/axios/dartsHubApi'
import { handleError } from '@/functions/utils'
import ConfirmModal from '@/components/ConfirmModal.vue'
import { useConfirm } from '@/composables/useConfirm'
import { RANKING_CATEGORIES, getCodeClt, type ImportRow, type SourceKind, type WizardParams } from './types'
import { parseThreeKGroupWins, parseThreeKPlacements } from './threeKDarts'

const props = defineProps<{ rows: ImportRow[]; source: SourceKind; params: WizardParams }>()
defineEmits<{ next: []; back: [] }>()

const { confirm } = useConfirm()

const rows = props.rows

const mode = ref<'paste' | 'excel' | 'dartup' | 'd4w' | '3kdarts'>(
    props.source === 'excel' || props.source === 'dartup' || props.source === 'd4w' || props.source === '3kdarts'
        ? props.source
        : 'paste'
)
const dartupCategory = ref('')
const dartupLoading = ref(false)
const dartupMessages = ref<string[]>([])

const CATEGORY_ORDER: string[] = RANKING_CATEGORIES.map((c) => c.value)

function categoryLabel(value: string): string {
    return RANKING_CATEGORIES.find((c) => c.value === value)?.label ?? value
}

// Comparaison aux inscriptions (cf. /leaderboard/import/check-inscriptions) : toutes les
// lignes à l'arrivée de nouvelles lignes, puis UNIQUEMENT la ligne corrigée, une fois la
// saisie validée (sortie du champ / Entrée), jamais pendant la frappe. Le résultat est gardé
// par ligne (identité d'objet) : une ligne en cours de correction reste en haut, en rouge,
// jusqu'à sa propre vérification. Les "inscrits absents" se recalculent ici, sans serveur.
interface RowCheck {
    registered: boolean
    incomplete: boolean
    halves: string[]
    half_ids: (number | null)[]
}
interface ExpectedInscription {
    id: number
    name: string
    categories: string[]
}

const checking = ref(false)
const checkDone = ref(false)
const rowChecks = shallowRef(new Map<ImportRow, RowCheck>())
const expectedInscriptions = shallowRef<ExpectedInscription[]>([])
// Dernière requête envoyée pour chaque ligne : une réponse plus ancienne est ignorée.
const rowCheckSeq = new Map<ImportRow, number>()
let checkSeq = 0
let pendingChecks = 0

async function checkRows(targets: ImportRow[]) {
    if (!props.params.eventId || !targets.length) return
    const seq = ++checkSeq
    targets.forEach((r) => rowCheckSeq.set(r, seq))
    pendingChecks++
    checking.value = true
    try {
        const { data } = await dartsHubApi.post<{ rows: RowCheck[]; expected: ExpectedInscription[] }>(
            '/leaderboard/import/check-inscriptions',
            {
                event_id: props.params.eventId,
                rows: targets.map((r) => ({ category: r.category, player_name_raw: r.player_name_raw })),
            },
            { timeout: 120000 }
        )
        const next = new Map(rowChecks.value)
        targets.forEach((r, i) => {
            if (rowCheckSeq.get(r) === seq && data.rows[i]) next.set(r, data.rows[i])
        })
        rowChecks.value = next
        expectedInscriptions.value = data.expected
        checkDone.value = true
    } catch (err) {
        handleError(err, 'comparaison aux inscriptions')
    } finally {
        pendingChecks--
        checking.value = pendingChecks > 0
    }
}

function checkAllRows() {
    return checkRows(rows.filter((r) => !rowChecks.value.has(r)))
}

// Nouvelles lignes (fichier, lien, collage) : seules celles jamais vérifiées partent.
watch(() => rows.length, (len, prev) => {
    if (len > (prev ?? 0)) checkAllRows()
})
onMounted(checkAllRows)

function isUnregistered(row: ImportRow): boolean {
    const c = rowChecks.value.get(row)
    return !!c && !c.registered
}
function isIncomplete(row: ImportRow): boolean {
    return !!rowChecks.value.get(row)?.incomplete
}

const flaggedCount = computed(() => rows.filter(isUnregistered).length)
const incompleteCount = computed(() => rows.filter(isIncomplete).length)

// Inscrits attendus dans un tableau présent, reconnus par aucune ligne de ce tableau.
const unmatchedByCategory = computed(() => {
    const matched = new Map<string, Set<number>>()
    for (const r of rows) {
        const ids = matched.get(r.category) ?? new Set<number>()
        rowChecks.value.get(r)?.half_ids.forEach((id) => id !== null && ids.add(id))
        matched.set(r.category, ids)
    }
    const out = new Map<string, ExpectedInscription[]>()
    for (const [category, ids] of matched) {
        out.set(category, expectedInscriptions.value.filter((e) => e.categories.includes(category) && !ids.has(e.id)))
    }
    return out
})
const filteredUnmatched = computed(() =>
    CATEGORY_ORDER.filter((c) => !categoryFilter.value || c === categoryFilter.value).flatMap((category) =>
        (unmatchedByCategory.value.get(category) ?? []).map((e) => ({ category, name: e.name }))
    )
)

// Choix d'un inscrit pour remplacer un joueur non reconnu (ou compléter une doublette) :
// d'abord les inscrits absents des résultats de ce tableau, puis tous les autres.
function candidateGroups(row: ImportRow): { absent: ExpectedInscription[]; others: ExpectedInscription[] } {
    const absent = unmatchedByCategory.value.get(row.category) ?? []
    const absentIds = new Set(absent.map((e) => e.id))
    const byName = (a: ExpectedInscription, b: ExpectedInscription) => a.name.localeCompare(b.name)
    return {
        absent: [...absent].sort(byName),
        others: expectedInscriptions.value.filter((e) => !absentIds.has(e.id)).sort(byName),
    }
}

// `halfIdx` = joueur à remplacer ; `null` = partenaire à ajouter (doublette incomplète).
function pickInscription(row: ImportRow, halfIdx: number | null, event: Event) {
    const select = event.target as HTMLSelectElement
    const name = select.value
    select.value = ''
    if (!name) return
    const halves = [...(rowChecks.value.get(row)?.halves ?? [row.player_name_raw])]
    if (halfIdx === null) halves.push(name)
    else halves[halfIdx] = name
    row.player_name_raw = halves.join(' / ')
    checkRows([row])
}

// Lignes en erreur toujours en haut, puis tri par catégorie (ordre de RANKING_CATEGORIES),
// ordre d'origine conservé à l'intérieur -- `idx` reste l'index dans `rows` (suppression).
const categoryFilter = ref('')
const presentCategories = computed(() =>
    RANKING_CATEGORIES.map((c) => ({ ...c, count: rows.filter((r) => r.category === c.value).length }))
        .filter((c) => c.count > 0)
)
const displayedRows = computed(() =>
    rows
        .map((row, idx) => {
            const unregistered = isUnregistered(row)
            const incomplete = isIncomplete(row)
            const check = rowChecks.value.get(row)
            const unknownHalves = check
                ? check.halves.map((h, i) => ({ h, i })).filter(({ i }) => check.half_ids[i] === null)
                : []
            return { row, idx, unregistered, incomplete, unknownHalves, flagged: unregistered || incomplete }
        })
        .filter(({ row }) => !categoryFilter.value || row.category === categoryFilter.value)
        .sort(
            (a, b) =>
                Number(b.flagged) - Number(a.flagged) ||
                CATEGORY_ORDER.indexOf(a.row.category) - CATEGORY_ORDER.indexOf(b.row.category) ||
                a.idx - b.idx
        )
)

// Ajout d'inscription pour un vrai participant absent des inscriptions (inscrit sur
// place) -- même endpoint que l'aperçu. Pas pour une faute de frappe : corriger le nom.
const registering = ref(false)

async function registerRows(targets: ImportRow[]) {
    if (!targets.length) return
    // Seuls les joueurs non reconnus sont créés (cf. quick-register) : pour une doublette
    // dont un seul joueur manque, le partenaire déjà inscrit n'est pas touché.
    const names = targets.flatMap((r) => {
        const c = rowChecks.value.get(r)
        return c ? c.halves.filter((_, i) => c.half_ids[i] === null) : [r.player_name_raw]
    })
    const accepted = await confirm(
        `Ajouter ${names.length} inscription(s) à ce tournoi : ${names.join(', ')} ? ` +
        'Seulement pour de vrais participants (inscrits sur place) -- pour une faute de frappe, ' +
        'corrigez plutôt le nom (cf. "Inscrits absents des résultats").'
    )
    if (!accepted) return
    registering.value = true
    try {
        const { data } = await dartsHubApi.post('/leaderboard/import/quick-register', {
            event_id: props.params.eventId,
            scope_level: props.params.scopeLevel,
            scope_id: props.params.scopeId,
            rows: targets.map((r) => ({ category: r.category, player_name_raw: r.player_name_raw })),
        })
        toast.success(`${data.created} inscription(s) ajoutée(s)${data.created ? ` : ${data.names.join(', ')}` : ''}.`)
        await checkRows(targets)
    } catch (err) {
        handleError(err, 'ajout des inscriptions')
    } finally {
        registering.value = false
    }
}
const pasteCategory = ref<string>(RANKING_CATEGORIES[0].value)
const tournamentMode = ref<'RR' | 'AB'>('RR')
const listAText = ref('')
const listBText = ref('')
const poolsText = ref('')

function splitLines(text: string): string[] {
    return text.split('\n').map((l) => l.trim()).filter(Boolean)
}

function addTableRows() {
    const listA = splitLines(listAText.value)
    const listB = tournamentMode.value === 'AB' ? splitLines(listBText.value) : []
    if (!listA.length && !listB.length) {
        toast.info('Aucun nom à ajouter.')
        return
    }
    // cf. l'ancien generate-ab (event_routes.py) : un nom présent dans la liste B (repêché)
    // n'apparaît plus dans la liste A, pour ne pas le compter deux fois.
    const bNamesUpper = new Set(listB.map((n) => n.toUpperCase()))
    const filteredA = listA.filter((n) => !bNamesUpper.has(n.toUpperCase()))

    filteredA.forEach((name, i) => {
        rows.push({
            category: pasteCategory.value,
            player_name_raw: name,
            code_clt: getCodeClt(i + 1, tournamentMode.value, 'A'),
        })
    })
    listB.forEach((name, i) => {
        rows.push({
            category: pasteCategory.value,
            player_name_raw: name,
            code_clt: getCodeClt(i + 1, 'AB', 'B'),
        })
    })
    listAText.value = ''
    listBText.value = ''
    toast.success(`${filteredA.length + listB.length} ligne(s) ajoutée(s).`)
}

function addPoolsRows() {
    const lines = splitLines(poolsText.value)
    if (!lines.length) {
        toast.info('Aucune ligne à ajouter.')
        return
    }
    let added = 0
    for (const line of lines) {
        const [name, winsRaw] = line.split(';').map((s) => s.trim())
        const wins = Number(winsRaw)
        if (!name || Number.isNaN(wins) || wins < 0 || wins > 15) continue
        rows.push({ category: pasteCategory.value, player_name_raw: name, code_clt: `P${wins}` })
        added++
    }
    poolsText.value = ''
    if (added < lines.length) toast.warning(`${added}/${lines.length} ligne(s) ajoutée(s) -- format attendu "Nom;victoires".`)
    else toast.success(`${added} ligne(s) ajoutée(s).`)
}

// 3K Darts. Comme addTableRows : un joueur passé en Tournoi B prend son résultat B à la
// place de celui du tableau A (place ou poules), quel que soit l'ordre des imports --
// convention du classement officiel AUV (Open Comité #1, 2026-10-17 : les repêchés du
// 1/32e n'y ont plus leur A33).
function mergeThreeKRows(parsed: ImportRow[], category: string, listType: 'A' | 'B', title: string) {
    const sameCategory = (r: ImportRow) => r.category === category
    const upper = (r: ImportRow) => r.player_name_raw.toUpperCase()
    let skipped = 0
    if (listType === 'B') {
        const bNames = new Set(parsed.map(upper))
        for (let i = rows.length - 1; i >= 0; i--) {
            const r = rows[i]
            if (sameCategory(r) && !r.code_clt.startsWith('B') && bNames.has(upper(r))) {
                rows.splice(i, 1)
                skipped++
            }
        }
    } else {
        const bNames = new Set(rows.filter((r) => sameCategory(r) && r.code_clt.startsWith('B')).map(upper))
        const before = parsed.length
        parsed = parsed.filter((r) => !bNames.has(upper(r)))
        skipped = before - parsed.length
    }
    rows.push(...parsed)
    threeKMessages.value.push(
        `${title} → ${categoryLabel(category)}, tableau ${listType} : ${parsed.length} ligne(s)` +
        (skipped ? ` (${skipped} joueur(s) du Tournoi B retiré(s) du tableau A)` : '')
    )
}

// Lien 3K Darts (épreuve ou liste de tournois) : lu côté serveur, cf. threek_darts.py.
interface ThreeKEvent {
    db_id: number
    event_id: number
    name: string
    date: string | null
    category: string
    list_type: 'A' | 'B'
    same_tournament: boolean
    selected: boolean
}

const threeKSource = ref<'link' | 'paste'>('link')
const threeKUrl = ref('')
const threeKLoading = ref(false)
const threeKEvents = ref<ThreeKEvent[]>([])
const selectedThreeKCount = computed(() => threeKEvents.value.filter((e) => e.selected).length)

async function searchThreeKEvents() {
    threeKLoading.value = true
    try {
        const { data } = await dartsHubApi.get<Omit<ThreeKEvent, 'selected'>[]>('/leaderboard/import/3kdarts/events', {
            params: { url: threeKUrl.value },
        })
        // Lien d'une épreuve : toutes les catégories de son tournoi, cochées d'office (un
        // tournoi s'importe en une fois) ; liste de tournois : à choisir.
        threeKEvents.value = data.map((e) => ({ ...e, selected: e.same_tournament }))
        if (!data.length) toast.info('Aucune épreuve trouvée pour ce lien.')
    } catch (err) {
        handleError(err, 'recherche des épreuves 3K Darts')
    } finally {
        threeKLoading.value = false
    }
}

async function importThreeKEvents() {
    if (!props.params.eventId) return
    threeKLoading.value = true
    try {
        // Tableaux A d'abord : un Tournoi B importé dans le même lot retire ensuite ses
        // joueurs du A (cf. mergeThreeKRows), dans un ordre ou dans l'autre.
        const selected = threeKEvents.value
            .filter((e) => e.selected)
            .sort((a, b) => a.list_type.localeCompare(b.list_type))
        for (const e of selected) {
            try {
                const { data } = await dartsHubApi.post<DartupParseResponse>('/leaderboard/import/3kdarts/parse', {
                    event_id: props.params.eventId,
                    db_id: e.db_id,
                    threek_event_id: e.event_id,
                    category: e.category,
                    list_type: e.list_type,
                })
                mergeThreeKRows(data.rows, e.category, e.list_type, data.title || e.name)
                if (data.absent.length) {
                    threeKMessages.value.push(`  ${data.absent.length} désinscrit(s) ignoré(s) : ${data.absent.join(', ')}`)
                }
                data.warnings.forEach((w) => threeKMessages.value.push(`  ⚠️ ${w}`))
                e.selected = false // déjà importé : évite un doublon si on relance
            } catch (err) {
                handleError(err, `lecture de l'épreuve 3K Darts "${e.name}"`)
            }
        }
    } finally {
        threeKLoading.value = false
    }
}

// Secours : réponse JSON "placement" collée (cf. threeKDarts.ts), un tableau à la fois.
const threeKCategory = ref<string>(RANKING_CATEGORIES[0].value)
const threeKListType = ref<'A' | 'B'>('A')
const threeKText = ref('')
const threeKMessages = ref<string[]>([])

function addThreeKRows() {
    let parsed: ImportRow[]
    let poolCount: number
    try {
        ;({ rows: parsed, poolCount } = parseThreeKPlacements(threeKText.value, threeKCategory.value, threeKListType.value))
    } catch (err) {
        toast.error((err as Error).message)
        return
    }
    if (!parsed.length) {
        toast.info('Aucun joueur classé dans le texte collé (tournoi pas encore terminé ?).')
        return
    }
    mergeThreeKRows(parsed, threeKCategory.value, threeKListType.value, 'texte collé')
    if (poolCount) {
        threeKMessages.value.push(
            `  ⚠️ ${poolCount} sorti(s) en poules sans code CLT : collez les groupes dans "Victoires de poules" ` +
            '(ou saisissez P + nombre de victoires, ex. P2).'
        )
    }
    threeKText.value = ''
}

// Victoires de poules (une réponse par groupe) -> P{victoires} pour les lignes de la
// catégorie sans code (sorties en poules) ; un joueur absent des lignes est ajouté.
const threeKGroupsText = ref('')

function applyThreeKGroupWins() {
    let wins: Map<string, number>
    try {
        wins = parseThreeKGroupWins(threeKGroupsText.value)
    } catch (err) {
        toast.error((err as Error).message)
        return
    }
    let filled = 0
    let added = 0
    for (const [name, win] of wins) {
        const row = rows.find(
            (r) => r.category === threeKCategory.value && r.player_name_raw.toUpperCase() === name.toUpperCase()
        )
        if (!row) {
            rows.push({ category: threeKCategory.value, player_name_raw: name, code_clt: `P${win}` })
            added++
        } else if (!row.code_clt) {
            row.code_clt = `P${win}`
            filled++
        }
    }
    const missing = rows.filter((r) => r.category === threeKCategory.value && !r.code_clt).length
    threeKMessages.value.push(
        `${categoryLabel(threeKCategory.value)}, poules : ${filled} code(s) complété(s)` +
        (added ? `, ${added} ligne(s) ajoutée(s)` : '') +
        (missing ? ` -- encore ${missing} sans code (groupe manquant ?)` : '')
    )
    threeKGroupsText.value = ''
}

async function onExcelFile(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0]
    if (!file) return
    const { default: ExcelJS } = await import('exceljs')
    const workbook = new ExcelJS.Workbook()
    await workbook.xlsx.load(await file.arrayBuffer())
    const sheet = workbook.worksheets[0]
    if (!sheet) { toast.error('Fichier Excel vide.'); return }

    const header = (sheet.getRow(1).values as any[]).map((v) => String(v ?? '').trim().toLowerCase())
    const catIdx = header.findIndex((h) => h.includes('cat'))
    const nameIdx = header.findIndex((h) => h.includes('joueur') || h.includes('nom'))
    const codeIdx = header.findIndex((h) => h.includes('code'))
    if (catIdx === -1 || nameIdx === -1 || codeIdx === -1) {
        toast.error('Colonnes introuvables -- attendu : Catégorie / Joueur / Code CLT.')
        return
    }

    let added = 0
    sheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return
        const values = row.values as any[]
        const category = String(values[catIdx] ?? '').trim()
        const player = String(values[nameIdx] ?? '').trim()
        const codeClt = String(values[codeIdx] ?? '').trim()
        if (!category || !player || !codeClt) return
        const known = RANKING_CATEGORIES.find((c) => c.value === category || c.label === category)
        rows.push({ category: known?.value ?? category, player_name_raw: player, code_clt: codeClt })
        added++
    })
    toast.success(`${added} ligne(s) importée(s) depuis le fichier.`)
    ;(event.target as HTMLInputElement).value = ''
}

interface DartupParseResponse {
    title: string
    category: string
    rows: ImportRow[]
    absent: string[]
    warnings: string[]
}

// Parsing côté serveur (cf. /leaderboard/import/dartup/parse) : c'est là que vivent les
// inscriptions, nécessaires pour compléter les doublettes (Dartup ne liste qu'une joueuse
// par équipe).
async function onDartupFiles(event: Event) {
    const input = event.target as HTMLInputElement
    const files = Array.from(input.files ?? [])
    if (!files.length || !props.params.eventId) return
    dartupLoading.value = true
    try {
        for (const file of files) {
            const form = new FormData()
            form.append('file', file)
            form.append('event_id', String(props.params.eventId))
            if (dartupCategory.value) form.append('category', dartupCategory.value)
            try {
                const { data } = await dartsHubApi.post<DartupParseResponse>('/leaderboard/import/dartup/parse', form, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                addDartupResult(data, file.name)
            } catch (err) {
                handleError(err, `lecture du fichier Dartup "${file.name}"`)
            }
        }
    } finally {
        dartupLoading.value = false
        input.value = ''
    }
}

function addDartupResult(data: DartupParseResponse, fallbackTitle: string) {
    rows.push(...data.rows)
    dartupMessages.value.push(`${data.title || fallbackTitle} → ${categoryLabel(data.category)} : ${data.rows.length} ligne(s)`)
    if (data.absent.length) {
        dartupMessages.value.push(`  ${data.absent.length} absent(s) ignoré(s) : ${data.absent.join(', ')}`)
    }
    data.warnings.forEach((w) => dartupMessages.value.push(`  ⚠️ ${w}`))
}

// Lien Dartup (page de recherche ou d'un tableau) : plus complet que l'export xlsx, qui
// omet les équipes formées sur place (cf. dartup_web.py).
interface DartupWebTournament {
    id: number
    name: string
    date: string | null
    category: string
    selected: boolean
}

const dartupSource = ref<'link' | 'file'>('link')
const dartupUrl = ref('')
const dartupTournaments = ref<DartupWebTournament[]>([])
const selectedTournamentCount = computed(() => dartupTournaments.value.filter((t) => t.selected).length)

async function searchDartupTournaments() {
    dartupLoading.value = true
    try {
        const { data } = await dartsHubApi.get<Omit<DartupWebTournament, 'selected'>[]>(
            '/leaderboard/import/dartup/web/tournaments',
            { params: { url: dartupUrl.value } }
        )
        dartupTournaments.value = data.map((t) => ({ ...t, selected: true }))
        if (!data.length) toast.info('Aucun tableau trouvé pour ce lien.')
    } catch (err) {
        handleError(err, 'recherche des tableaux Dartup')
    } finally {
        dartupLoading.value = false
    }
}

async function importDartupTournaments() {
    if (!props.params.eventId) return
    dartupLoading.value = true
    try {
        for (const t of dartupTournaments.value.filter((x) => x.selected)) {
            try {
                const { data } = await dartsHubApi.post<DartupParseResponse>('/leaderboard/import/dartup/web/parse', {
                    event_id: props.params.eventId,
                    tournament_id: t.id,
                    category: t.category,
                })
                addDartupResult(data, t.name)
                t.selected = false // déjà importé : évite un doublon si on relance
            } catch (err) {
                handleError(err, `lecture du tableau Dartup "${t.name}"`)
            }
        }
    } finally {
        dartupLoading.value = false
    }
}

// Base Darts4Windows (.dfw) : lue côté serveur (cf. darts4windows.py), un fichier = tous
// les tableaux de la journée. L'admin choisit ensuite les tableaux et leur catégorie.
interface D4wTableau {
    key: string
    title: string
    pool_title: string | null
    date: string | null
    category: string
    rows: ImportRow[]
    absent: string[]
    warnings: string[]
    file: string
    selected: boolean
    imported: boolean
}

const d4wLoading = ref(false)
const d4wTableaux = ref<D4wTableau[]>([])
const d4wMessages = ref<string[]>([])
const selectedD4wCount = computed(() => d4wTableaux.value.filter((t) => t.selected).length)

// Plusieurs bases possibles (une par jour, une par catégorie...) : les tableaux de chaque
// fichier s'ajoutent à la liste -- un fichier déjà lu (même nom) est remplacé, sauf ses
// tableaux déjà ajoutés aux lignes.
async function onD4wFiles(event: Event) {
    const input = event.target as HTMLInputElement
    const files = Array.from(input.files ?? [])
    if (!files.length) return
    d4wLoading.value = true
    try {
        for (const file of files) {
            const form = new FormData()
            form.append('file', file)
            try {
                const { data } = await dartsHubApi.post<{ tableaux: Omit<D4wTableau, 'file' | 'selected' | 'imported'>[] }>(
                    '/leaderboard/import/d4w/parse',
                    form,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                )
                d4wTableaux.value = [
                    ...d4wTableaux.value.filter((t) => t.file !== file.name || t.imported),
                    ...data.tableaux.map((t) => ({ ...t, file: file.name, selected: true, imported: false })),
                ]
                if (!data.tableaux.length) toast.info(`Aucun tableau joué dans "${file.name}".`)
            } catch (err) {
                handleError(err, `lecture du fichier Darts4Windows "${file.name}"`)
            }
        }
    } finally {
        d4wLoading.value = false
        input.value = ''
    }
}

function importD4wTableaux() {
    for (const t of d4wTableaux.value.filter((x) => x.selected)) {
        rows.push(...t.rows.map((r) => ({ ...r, category: t.category })))
        d4wMessages.value.push(`${t.title} → ${categoryLabel(t.category)} : ${t.rows.length} ligne(s)`)
        if (t.absent.length) {
            d4wMessages.value.push(`  ${t.absent.length} absent(s) ignoré(s) : ${t.absent.join(', ')}`)
        }
        t.warnings.forEach((w) => d4wMessages.value.push(`  ⚠️ ${w}`))
        t.selected = false
        t.imported = true // déjà ajouté : évite un doublon si on relance
    }
}
</script>

<style scoped>
.step-rows h3 {
    margin-bottom: 1rem;
}

.mode-tabs {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.mode-tab {
    padding: 0.5rem 1rem;
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: var(--radius, 8px);
    background: transparent;
    cursor: pointer;
}

.mode-tab.active {
    background: var(--color-accent, #1a3a5c);
    color: #fff;
    border-color: var(--color-accent, #1a3a5c);
}

.subsection {
    margin: 1.25rem 0;
    padding-top: 1rem;
    border-top: 1px solid var(--color-light-shadow, #ddd);
}

.subsection h4 {
    margin-bottom: 0.5rem;
}

.form-group {
    margin-bottom: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    max-width: 420px;
}

.form-group label {
    font-weight: 600;
    font-size: 0.9rem;
}

.form-textarea {
    font-family: monospace;
    font-size: 0.85rem;
    padding: 0.5rem;
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: var(--radius, 8px);
    resize: vertical;
    max-width: 420px;
}

.hint {
    font-size: 0.85rem;
    color: var(--color-fg-muted, #888);
    margin: 0.15rem 0 0.5rem;
}

.dartup-link-row {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-bottom: 0.75rem;
}

.dartup-link-row .form-input {
    flex: 1 1 320px;
}

.dartup-tournaments {
    margin-bottom: 0.75rem;
}

.dartup-messages {
    margin: 0.75rem 0 0;
    padding-left: 1.1rem;
    font-size: 0.85rem;
    white-space: pre-wrap;
}

.rows-toolbar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-bottom: 0.5rem;
}

.category-filter {
    width: auto;
}

.unmatched-block {
    margin: 0.75rem 0 1rem;
    padding: 0.75rem 1rem;
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: var(--radius, 8px);
    font-size: 0.85rem;
}

.unmatched-block ul {
    margin: 0;
    padding-left: 1.1rem;
    columns: 2 260px;
}

.unmatched-cat {
    color: var(--color-fg-muted, #888);
}

.row-issue {
    background: rgba(217, 83, 79, 0.08);
}

.name-input {
    min-width: 240px;
}

.pick-inscription {
    margin-top: 0.3rem;
    display: block;
}

.btn-secondary.btn-sm {
    padding: 0.2rem 0.6rem;
    font-size: 0.8rem;
    margin-left: 0.4rem;
}

.no-win-cell {
    text-align: center;
}

.issue-cell {
    color: var(--color-danger, #d9534f);
    font-size: 0.85rem;
    white-space: nowrap;
}

.rows-table-wrapper {
    margin-top: 1.5rem;
}

.rows-table {
    width: 100%;
    border-collapse: collapse;
}

.rows-table th,
.rows-table td {
    padding: 0.35rem 0.5rem;
    border-bottom: 1px solid var(--color-light-shadow, #eee);
    text-align: left;
}

.form-input-sm {
    width: 100%;
    padding: 0.3rem 0.4rem;
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: 4px;
    font-size: 0.85rem;
}

.code-clt-input {
    max-width: 90px;
}

.row-delete {
    border: none;
    background: transparent;
    cursor: pointer;
    color: var(--color-danger, #d9534f);
    font-size: 1.1rem;
}

.form-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.5rem;
}

.btn-submit,
.btn-secondary {
    display: inline-flex;
    align-items: center;
    padding: 0.6rem 1.2rem;
    border-radius: var(--radius, 8px);
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--color-accent, #1a3a5c);
}

.btn-submit {
    background: var(--color-accent, #1a3a5c);
    color: #fff;
}

.btn-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-secondary {
    background: transparent;
    color: var(--color-accent, #1a3a5c);
}
</style>
